// src/components/TablePagination.tsx
import * as React from "react";
import {
  Box,
  IconButton,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import usePagination from "@mui/material/usePagination";
import {
  FirstPage,
  LastPage,
  KeyboardArrowLeftRounded,
  KeyboardArrowRightRounded,
} from "@mui/icons-material";

type TablePaginationCustomProps = {
  component?: React.ElementType;

  count: number;

  page: number;

  rowsPerPage: number;

  rowsPerPageOptions?: number[];
  /** Callback ao mudar de página (0-based) */
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => void;
  /** Callback ao mudar rowsPerPage */
  onRowsPerPageChange?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;

  /** Mostrar botão de primeira página */
  showFirstButton?: boolean;
  /** Mostrar botão de última página */
  showLastButton?: boolean;
  /** Mostrar textos “Mostrando itens ...” e “Mostrando um total de ...” */
  showMilestones?: boolean;

  /** Props extras para o container raiz (className, sx, etc.) */
  className?: string;
  sx?;
};

const List = styled("ul")({
  display: "flex",
  justifyContent: "flex-end",
  listStyle: "none",
  padding: 0,
  margin: 0,
});

const Item = styled("li")<{ selected?: boolean }>(({ selected }) => ({
  "& button": {
    outline: "none",
    cursor: "pointer",
    backgroundColor: selected ? "#FFFFFF" : "#F5F5F5",
    border: 0,
    color: selected ? "#D91F05" : "#3E3D3D",
    width: 32,
    height: 36,
    fontSize: 16,
    fontFamily: "Montserrat, Roboto, sans-serif",
    borderRadius: 4,
    fontWeight: "bold",
    marginLeft: "1px",
  },
}));

const TablePagination: React.FC<TablePaginationCustomProps> = (props) => {
  const {
    component: RootComponentProp = "div",
    count,
    page,
    rowsPerPage,
    rowsPerPageOptions = [10, 25, 50],
    onPageChange,
    onRowsPerPageChange,
    showFirstButton = true,
    showLastButton = true,
    showMilestones = true,
    className,
    sx,
  } = props;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.only("xs"));

  const pagesTotal =
    rowsPerPage > 0 ? Math.max(1, Math.ceil(count / rowsPerPage)) : 0;

  // usePagination trabalha com página 1-based
  const { items } = usePagination({
    count: pagesTotal,
    page: page + 1,
    showFirstButton,
    showLastButton,
  });

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage1Based: number
  ) => {
    const newPage0Based = newPage1Based - 1;
    onPageChange(event, newPage0Based);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    onRowsPerPageChange?.(event);
  };

  // Cálculos de itens exibidos
  const from = count === 0 ? 0 : page * rowsPerPage + 1;
  const to = count === 0 ? 0 : Math.min(count, (page + 1) * rowsPerPage);
  const currentPageCount =
    count === 0 ? 0 : Math.min(rowsPerPage, count - page * rowsPerPage);

  return (
    <Box
      component={RootComponentProp}
      className={className}
      sx={{
        mt: 2,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        ...sx,
      }}
    >
      {/* LADO ESQUERDO: textos + rowsPerPage */}
      {showMilestones && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography variant="body1">
            <strong>Mostrando itens </strong>
            <strong style={{ color: theme.palette.primary.main }}>
              {count === 0 ? "0" : `${from}-${to}`}
            </strong>{" "}
            de um total <strong>{count}</strong>
          </Typography>

          <Typography variant="body2" sx={{ display: "none" }}>
            Total de{" "}
            <span style={{ color: "#FFB03A" }}>{currentPageCount}</span> por
            linha
          </Typography>
        </Box>
      )}

      {/* LADO DIREITO: botões de navegação com estilo antigo */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignContent: "baseline",
          justifyContent: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mt: 0, gap: 1 }}>
          {onRowsPerPageChange && (
            <React.Fragment>
              <Typography variant="body1">Linhas por página:</Typography>
              <Select
                variant="outlined"
                size="small"
                value={rowsPerPage}
                onChange={handleRowsPerPageChange as any}
              >
                {rowsPerPageOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </React.Fragment>
          )}
        </Box>
        <List>
          {items.map(({ page: itemPage, type, selected, ...item }, index) => {
            let children: React.ReactNode = null;

            if (type === "start-ellipsis" || type === "end-ellipsis") {
              children = (
                <div
                  style={{
                    marginTop: 8,
                    fontSize: 15,
                    fontWeight: "bold",
                  }}
                >
                  ...
                </div>
              );
            } else if (type === "page") {
              children = (
                <button
                  type="button"
                  disabled={item.disabled}
                  onClick={(e) => {
                    handleChangePage(e, itemPage ?? 1);
                    item.onClick?.(e);
                  }}
                  style={{
                    fontSize: isMobile ? 11 : 16,
                    width: isMobile ? 25 : 32,
                  }}
                >
                  {itemPage}
                </button>
              );
            } else if (type === "first" || type === "last") {
              children = (
                <IconButton
                  disabled={item.disabled}
                  type="button"
                  onClick={(e) => {
                    handleChangePage(e, itemPage ?? 1);
                    item.onClick?.(e);
                  }}
                  sx={{ width: isMobile ? 25 : 32 }}
                >
                  {type === "first" ? <FirstPage /> : <LastPage />}
                </IconButton>
              );
            } else {
              const icon =
                type === "next" ? (
                  <KeyboardArrowRightRounded />
                ) : (
                  <KeyboardArrowLeftRounded />
                );

              children = (
                <IconButton
                  disabled={item.disabled}
                  type="button"
                  onClick={(e) => {
                    handleChangePage(e, itemPage ?? 1);
                    item.onClick?.(e);
                  }}
                  sx={{ width: isMobile ? 25 : 32 }}
                >
                  {icon}
                </IconButton>
              );
            }

            return (
              <Item key={index} selected={selected}>
                {children}
              </Item>
            );
          })}
        </List>
      </Box>
    </Box>
  );
};

export default TablePagination;
